import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Grid,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import api from '../services/api';

const SmsStatus = ({ complaintId, onRefresh }) => {
  const [smsStatus, setSmsStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendDialog, setResendDialog] = useState(false);
  const [selectedSmsType, setSelectedSmsType] = useState('confirmation');

  useEffect(() => {
    if (complaintId) {
      fetchSmsStatus();
      const interval = setInterval(() => {
        fetchSmsStatus();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [complaintId]);

  const fetchSmsStatus = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔍 Fetching SMS status for complaintId:', complaintId);
      const response = await api.get(`/complaints/${complaintId}/sms-status`);
      console.log('📱 SMS Status Response:', response.data);
      setSmsStatus(response.data);
    } catch (error) {
      console.error('❌ Error fetching SMS status:', error);
      console.error('❌ Error details:', error.response?.data);
      setError('Failed to fetch SMS status');
    } finally {
      setLoading(false);
    }
  };

  const handleResendSms = async () => {
    setResendLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await api.post(`/complaints/${complaintId}/resend-sms`, {
        smsType: selectedSmsType
      });
      
      setSuccess(`SMS resent successfully!`);
      setResendDialog(false);
      
      // Refresh SMS status
      await fetchSmsStatus();
      
      // Call parent refresh if provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error resending SMS:', error);
      setError(error.response?.data?.error || 'Failed to resend SMS');
    } finally {
      setResendLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon />;
      case 'failed':
        return <ErrorIcon />;
      case 'pending':
        return <ScheduleIcon />;
      default:
        return <MessageIcon />;
    }
  };

  const formatStatusLabel = (status) => {
    if (!status) return 'NOT_ATTEMPTED';
    if (status === 'pending') return 'PROCESSING';
    return String(status).toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not sent';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading SMS status...</Typography>
      </Box>
    );
  }

  if (!smsStatus) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No SMS status available</Alert>
      </Box>
    );
  }

  const confirmationSms = smsStatus.confirmation || {};
  const statusUpdateSms = smsStatus.statusUpdate || {};

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MessageIcon />
          SMS Status
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchSmsStatus}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Confirmation SMS Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Confirmation SMS
                </Typography>
                <Chip
                  icon={getStatusIcon(confirmationSms.status)}
                  label={formatStatusLabel(confirmationSms.status)}
                  color={getStatusColor(confirmationSms.status)}
                  size="small"
                />
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Sent At: {formatDate(confirmationSms.sentAt)}
                </Typography>
              </Box>
              
              {confirmationSms.error && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="error">
                    Error: {confirmationSms.error}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <Button variant="outlined" size="small" onClick={() => { setResendDialog(true); setSelectedSmsType('confirmation'); }}>
                Resend Confirmation SMS
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Update SMS Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Status Update SMS
                </Typography>
                <Chip
                  icon={getStatusIcon(statusUpdateSms.status)}
                  label={formatStatusLabel(statusUpdateSms.status)}
                  color={getStatusColor(statusUpdateSms.status)}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Sent At: {formatDate(statusUpdateSms.sentAt)}
                </Typography>
              </Box>

              {statusUpdateSms.error && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="error">
                    Error: {statusUpdateSms.error}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <Button variant="outlined" size="small" onClick={() => { setResendDialog(true); setSelectedSmsType('status_update'); }}>
                Resend Status Update SMS
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Resend Dialog */}
      <Dialog open={resendDialog} onClose={() => setResendDialog(false)}>
        <DialogTitle>Resend SMS</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to resend the {selectedSmsType === 'confirmation' ? 'Confirmation' : 'Status Update'} SMS?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendDialog(false)} disabled={resendLoading}>Cancel</Button>
          <Button onClick={handleResendSms} variant="contained" disabled={resendLoading}>
            {resendLoading ? 'Sending...' : 'Resend'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmsStatus; 